import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { db, Orchard } from '../db';
import { naturalCompareBy } from '../utils/naturalCompare';
import {
  getPhenologyData,
  recordPetalFallDate,
  PhenologyData,
} from '../services/phenologyService';
import { savePetalFallLocally } from '../services/phenologySyncService';
import PageHeader from './shell/PageHeader';
import { FormShell, FormSection, Field, ComputedValue, SaveBar, DataTable, Modal } from './patterns';
import type { DataTableColumn, SaveBarStatus } from './patterns';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useFormDirty } from '../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../hooks/useConfirmDiscard';
import { formatDate } from '../dateFormat';

/**
 * PhenologyCapture — Field app section for recording the 80% petal fall date.
 *
 * Flow:
 * 1. User selects an orchard from the offline-cached list.
 * 2. The current active petal fall date (if any) is displayed.
 * 3. User enters a new date and clicks Save.
 * 4. If an existing date would be overwritten, a Comparison View dialog
 *    shows "Previous Date → New Date" and asks for confirmation.
 * 5. On confirm, the date is recorded via the Phenology API.
 */
const PhenologyCapture: React.FC = () => {
  const { t } = useTranslation();

  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [selectedOrchardId, setSelectedOrchardId] = useState('');
  const [date, setDate] = useState('');
  const [phenology, setPhenology] = useState<PhenologyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);

  const { dirty } = useFormDirty(
    { selectedOrchardId: '', date: '' },
    { selectedOrchardId, date },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  const apiError: string | null = message?.type === 'danger' ? message.text : null;

  const navigate = useNavigate();

  // Comparison dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDate, setPendingDate] = useState('');

  // Pagination for orchard summary table
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nav.scouting'), to: '/scouting' },
    { label: t('scouting.phenology.title') },
  ]);

  // Load orchards from IndexedDB
  useEffect(() => {
    db.orchards
      .toArray()
      .then((list) => setOrchards(list.sort(naturalCompareBy((o) => o.orchardName))))
      .catch((err) => console.error('Failed to load orchards:', err));
  }, []);

  // Fetch phenology data when orchard changes
  useEffect(() => {
    if (!selectedOrchardId) {
      setPhenology(null);
      return;
    }
    setLoading(true);
    getPhenologyData(selectedOrchardId)
      .then((data) => setPhenology(data))
      .catch(() => setPhenology(null))
      .finally(() => setLoading(false));
  }, [selectedOrchardId]);

  const currentDate = phenology?.activePetalFallDate
    ? new Date(phenology.activePetalFallDate).toISOString().split('T')[0]
    : null;

  const handleSave = () => {
    if (!date || !selectedOrchardId) return;

    // If there's an existing date, show comparison dialog
    if (currentDate) {
      setPendingDate(date);
      setShowConfirm(true);
      return;
    }

    submitDate(date);
  };

  const submitDate = async (dateToSubmit: string) => {
    setShowConfirm(false);
    setSaving(true);
    setMessage(null);

    try {
      if (navigator.onLine) {
        const result = await recordPetalFallDate(selectedOrchardId, dateToSubmit);
        setPhenology({
          activePetalFallDate: result.activePetalFallDate,
          petalFallHistory: result.petalFallHistory,
        });
      } else {
        // Offline: save locally for background sync
        await savePetalFallLocally(selectedOrchardId, dateToSubmit);
      }
      // Update the orchard list so the summary table reflects the new date immediately
      setOrchards((prev) =>
        prev.map((o) =>
          o.id === selectedOrchardId
            ? { ...o, activePetalFallDate: new Date(dateToSubmit).toISOString() }
            : o
        )
      );
      setDate('');
      setMessage({ type: 'success', text: t('phenology.saveSuccess') });
    } catch {
      setMessage({ type: 'danger', text: t('phenology.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const pageOrchards = useMemo(
    () => orchards.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [orchards, currentPage]
  );

  const phenologyColumns: DataTableColumn<Orchard>[] = [
    {
      key: 'orchardName',
      label: t('phenology.orchard'),
      render: (o) => o.orchardName,
    },
    {
      key: 'activePetalFallDate',
      label: t('phenology.petalFallDate'),
      render: (o) =>
        o.activePetalFallDate
          ? formatDate(o.activePetalFallDate, { timeZone: 'UTC' })
          : '—',
    },
  ];

  const sections = [
    { id: 'record', title: t('scouting.phenology.sections.record') },
    { id: 'summary', title: t('scouting.phenology.sections.summary') },
  ];

  const saveBarStatus: SaveBarStatus = saving
    ? { kind: 'saving' }
    : apiError
      ? { kind: 'error', message: apiError }
      : dirty
        ? { kind: 'dirty' }
        : null;

  const saveBar = (
    <SaveBar
      status={saveBarStatus}
      primaryAction={{
        label: saving
          ? t('patterns.saveBar.primary.saving')
          : apiError
            ? t('patterns.saveBar.primary.retry')
            : t('patterns.saveBar.primary.save'),
        onClick: handleSave,
        disabled: (!dirty && !apiError) || !date || saving,
      }}
      secondaryActions={[
        {
          label: t('common.cancel'),
          tone: 'subtle',
          onClick: () => { if (confirmDiscard(dirty)) navigate('/scouting'); },
          disabled: saving,
        },
      ]}
    />
  );

  return (
    <div data-testid="phenology-capture">
      <PageHeader title={t('scouting.phenology.title')} meta={t('scouting.phenology.meta')} />

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <FormShell sections={sections} saveBar={saveBar}>
        <FormSection id="record" title={t('scouting.phenology.sections.record')}>
          <Field label={t('phenology.orchard')} controlId="phenologyOrchard">
            <select
              id="phenologyOrchard"
              aria-label={t('phenology.orchard')}
              value={selectedOrchardId}
              onChange={(e) => setSelectedOrchardId(e.target.value)}
            >
              <option value="">{t('phenology.selectOrchard')}</option>
              {orchards.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.orchardName}
                </option>
              ))}
            </select>
          </Field>

          {selectedOrchardId && loading && <Spinner animation="border" size="sm" />}

          {selectedOrchardId && currentDate && (
            <ComputedValue
              premise={t('phenology.currentDate')}
              value={currentDate}
            />
          )}

          {selectedOrchardId && (
            <Field label={t('phenology.petalFallDate')} controlId="phenologyDate">
              <input
                id="phenologyDate"
                type="date"
                aria-label={t('phenology.petalFallDate')}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
          )}
        </FormSection>

        <FormSection id="summary" title={t('scouting.phenology.sections.summary')}>
          {orchards.length > 0 && (
            <DataTable
              columns={phenologyColumns}
              rows={pageOrchards}
              rowKey={(o) => o.id}
              pagination={{
                page: currentPage,
                pageSize: PAGE_SIZE,
                total: orchards.length,
                onPageChange: setCurrentPage,
              }}
            />
          )}
        </FormSection>
      </FormShell>

      {/* Comparison modal — pat-* Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} title={t('phenology.confirmTitle')}>
        <Modal.Body>
          <p>{t('phenology.confirmMessage')}</p>
          <div className="d-flex justify-content-around align-items-center my-3">
            <div className="text-center">
              <small className="text-muted">{t('phenology.previousDate')}</small>
              <div className="fs-5">{currentDate}</div>
            </div>
            <div className="fs-4">→</div>
            <div className="text-center">
              <small className="text-muted">{t('phenology.newDate')}</small>
              <div className="fs-5 fw-bold">{pendingDate}</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setShowConfirm(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="pat-btn pat-btn--primary" onClick={() => submitDate(pendingDate)}>
            {t('phenology.confirm')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PhenologyCapture;
